/**
 * Hook générique pour gérer les filtres de pagination offset-based
 * Fournit un état centralisé et des helpers pour les pages de liste
 */

import { useState, useCallback } from 'react';
import {
  IOffsetPaginationQuery,
  PedagogicContentState,
  SortOrder,
} from '@sphere/shared/domain';

export interface PaginationFilters extends IOffsetPaginationQuery {
  q?: string;
  // Filtres spécifiques au school domain
  classId?: string;
  establishmentId?: string;
  schoolYearId?: string;
  includeStates?: PedagogicContentState[];
  chapterId?: string;
  teachingId?: string;
}

export interface UsePaginationFiltersReturn {
  filters: PaginationFilters;
  setFilters: React.Dispatch<React.SetStateAction<PaginationFilters>>;

  // Helpers pour modifier des filtres spécifiques
  setSearch: (q: string) => void;
  setLimit: (limit: number) => void;
  setPage: (page: number) => void;
  setSort: (sort: string, order?: SortOrder) => void;
  setClassId: (classId: string | undefined) => void;
  setEstablishmentId: (establishmentId: string | undefined) => void;
  setSchoolYearId: (schoolYearId: string | undefined) => void;
  setIncludeStates: (states: PedagogicContentState[]) => void;
  setChapterId: (chapterId: string | undefined) => void;
  setTeachingId: (teachingId: string | undefined) => void;

  // Helper pour reset tous les filtres
  resetFilters: () => void;

  // Helper pour reset seulement la page (utile quand on change un filtre)
  resetPage: () => void;
}

const DEFAULT_FILTERS: PaginationFilters = {
  page: 1,
  limit: 20,
  sort: 'createdAt',
  order: SortOrder.DESC,
};

/**
 * Hook pour gérer l'état des filtres de pagination
 *
 * @example
 * ```tsx
 * function TeachingsListPage() {
 *   const {
 *     filters,
 *     setSearch,
 *     setIncludeStates,
 *     resetPage
 *   } = usePaginationFilters();
 *
 *   const { data } = useTeachings(filters);
 *
 *   const handleSearchChange = (q: string) => {
 *     setSearch(q);
 *     resetPage(); // Reset page quand on change la recherche
 *   };
 *
 *   return (
 *     <div>
 *       <input onChange={(e) => handleSearchChange(e.target.value)} />
 *       {data?.data.map(...)}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePaginationFilters(
  initialFilters: Partial<PaginationFilters> = {}
): UsePaginationFiltersReturn {
  const [filters, setFilters] = useState<PaginationFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  const setSearch = useCallback((q: string) => {
    setFilters((prev) => ({ ...prev, q: q || undefined, page: 1 }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setFilters((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setSort = useCallback(
    (sort: string, order: SortOrder = SortOrder.DESC) => {
      setFilters((prev) => ({ ...prev, sort, order, page: 1 }));
    },
    []
  );

  const setClassId = useCallback((classId: string | undefined) => {
    setFilters((prev) => ({ ...prev, classId, page: 1 }));
  }, []);

  const setEstablishmentId = useCallback(
    (establishmentId: string | undefined) => {
      setFilters((prev) => ({ ...prev, establishmentId, page: 1 }));
    },
    []
  );

  const setSchoolYearId = useCallback((schoolYearId: string | undefined) => {
    setFilters((prev) => ({ ...prev, schoolYearId, page: 1 }));
  }, []);

  const setIncludeStates = useCallback(
    (includeStates: PedagogicContentState[]) => {
      setFilters((prev) => ({
        ...prev,
        includeStates: includeStates.length > 0 ? includeStates : undefined,
        page: 1,
      }));
    },
    []
  );

  const setChapterId = useCallback((chapterId: string | undefined) => {
    setFilters((prev) => ({ ...prev, chapterId, page: 1 }));
  }, []);

  const setTeachingId = useCallback((teachingId: string | undefined) => {
    setFilters((prev) => ({ ...prev, teachingId, page: 1 }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const resetPage = useCallback(() => {
    setFilters((prev) => ({ ...prev, page: 1 }));
  }, []);

  return {
    filters,
    setFilters,
    setSearch,
    setLimit,
    setPage,
    setSort,
    setClassId,
    setEstablishmentId,
    setSchoolYearId,
    setIncludeStates,
    setChapterId,
    setTeachingId,
    resetFilters,
    resetPage,
  };
}
